<script setup lang="ts">
import { ref, reactive, computed, onMounted, watch } from "vue";
import draggable from "vuedraggable";

// Components
import TextBlock from "./components/blocks/TextBlock.vue";
import DataBlock from "./components/blocks/DataBlock.vue";
import TableBlock from "./components/blocks/TableBlock.vue";
import ImageBlock from "./components/blocks/ImageBlock.vue";
import TextEdit from "./components/editors/TextEdit.vue";
import DataEdit from "./components/editors/DataEdit.vue";
import TableEdit from "./components/editors/TableEdit.vue";
import ImageEdit from "./components/editors/ImageEdit.vue";

// Interfaces
interface Block {
  id: string;
  type: "text" | "data" | "table" | "image";
  data: any;
  style: any;
}

interface Page {
  id: string;
  header?: {
    title: string;
    subtitle: string;
  };
  footer?: {
    content: string;
  };
  blocks: Block[];
}

interface Report {
  title: string;
  pages: Page[];
  formData: any;
}

// Props
const props = defineProps<{
  formData: any;
}>();

// Estado
const report = reactive<Report>({
  title: "Relatório de Higiene Ocupacional",
  pages: [],
  formData: {},
});

const selectedBlock = ref<Block | null>(null);
const currentPageIndex = ref(0);
const isLoading = ref(true);

// Computed com proteção contra undefined
const currentPage = computed(() => {
  return report.pages[currentPageIndex.value] || createNewPage();
});

// Inicialização
onMounted(() => {
  initializeReport();
});

// Métodos (mantidos do código anterior corrigido)
function initializeReport() {
  try {
    isLoading.value = true;
    report.formData = props.formData || {};

    if (report.pages.length === 0) {
      report.pages = [createNewPage()];
    }

    if (!report.pages[currentPageIndex.value]) {
      currentPageIndex.value = 0;
    }

    const firstPage = report.pages[0];
    if (firstPage.blocks.length === 0) {
      firstPage.blocks.push(
        createTextBlock("<h2>Relatório de Higiene Ocupacional</h2>")
      );
      firstPage.blocks.push(
        createDataBlock("dados-gerais", "Dados Gerais da Avaliação")
      );
      firstPage.blocks.push(
        createTableBlock("parametros", "Parâmetros Avaliados")
      );
      firstPage.blocks.push(
        createTextBlock(
          "<h3>Observações e Recomendações</h3><p>Digite as observações aqui...</p>"
        )
      );
    }
  } catch (error) {
    console.error("Erro ao inicializar relatório:", error);
    report.pages = [createNewPage()];
  } finally {
    isLoading.value = false;
  }
}

function createNewPage(): Page {
  return {
    id: generateId(),
    header: {
      title: "Relatório de Higiene Ocupacional",
      subtitle: "Sistema de Gestão de Higiene Ocupacional",
    },
    footer: {
      content: `Página 1 | Emitido em ${new Date().toLocaleDateString("pt-BR")}`,
    },
    blocks: [],
  };
}

function createTextBlock(content: string): Block {
  return {
    id: generateId(),
    type: "text",
    data: { content },
    style: { fontSize: "14px", lineHeight: "1.6" },
  };
}

function createDataBlock(dataKey: string, title: string): Block {
  return {
    id: generateId(),
    type: "data",
    data: {
      dataKey,
      title,
      fields: getDataFields(dataKey),
    },
    style: { backgroundColor: "#f8f9fa", padding: "15px", borderRadius: "5px" },
  };
}

function createTableBlock(dataKey: string, title: string): Block {
  return {
    id: generateId(),
    type: "table",
    data: {
      dataKey,
      title,
      columns: getTableColumns(dataKey),
      data: getTableData(dataKey),
    },
    style: { margin: "20px 0" },
  };
}

function createImageBlock(): Block {
  return {
    id: generateId(),
    type: "image",
    data: { src: "", alt: "Imagem do relatório", caption: "" },
    style: { textAlign: "center", margin: "20px 0" },
  };
}

// Funções auxiliares com proteção
function getDataFields(dataKey: string) {
  const fieldsMap: any = {
    "dados-gerais": [
      { label: "Local", value: report.formData?.local || "" },
      {
        label: "Data da Avaliação",
        value: report.formData?.assessmentDate || "",
      },
      { label: "Avaliador", value: report.formData?.evaluator || "" },
      { label: "Empresa", value: report.formData?.company || "" },
    ],
  };
  return fieldsMap[dataKey] || [];
}

function getTableColumns(dataKey: string) {
  const columnsMap: any = {
    parametros: [
      { key: "parameter", label: "Parâmetro", editable: true },
      { key: "value", label: "Valor Medido", editable: true },
      { key: "limit", label: "Limite", editable: true },
      { key: "unit", label: "Unidade", editable: true },
      { key: "status", label: "Status", editable: false },
    ],
  };
  return columnsMap[dataKey] || [];
}

function getTableData(dataKey: string) {
  const dataMap: any = {
    parametros: (report.formData?.parameters || []).map((param: any) => ({
      parameter: param.name || "",
      value: param.measuredValue || "",
      limit: param.limit || "",
      unit: param.unit || "",
      status:
        param.measuredValue && param.limit && param.measuredValue <= param.limit
          ? "Dentro"
          : "Acima",
    })),
  };
  return dataMap[dataKey] || [];
}

// Métodos de Navegação
function previousPage() {
  if (currentPageIndex.value > 0) {
    currentPageIndex.value--;
    selectedBlock.value = null;
  }
}

function nextPage() {
  if (currentPageIndex.value < report.pages.length - 1) {
    currentPageIndex.value++;
    selectedBlock.value = null;
  }
}

// Métodos do Editor
function addPage() {
  const newPage = createNewPage();
  report.pages.push(newPage);
  currentPageIndex.value = report.pages.length - 1;
}

function removePage(pageIndex: number) {
  if (report.pages.length > 1) {
    report.pages.splice(pageIndex, 1);
    if (currentPageIndex.value >= pageIndex && currentPageIndex.value > 0) {
      currentPageIndex.value--;
    }
  }
}

function movePageUp(pageIndex: number) {
  if (pageIndex > 0) {
    const page = report.pages.splice(pageIndex, 1)[0];
    report.pages.splice(pageIndex - 1, 0, page);
    if (currentPageIndex.value === pageIndex) {
      currentPageIndex.value = pageIndex - 1;
    }
  }
}

function movePageDown(pageIndex: number) {
  if (pageIndex < report.pages.length - 1) {
    const page = report.pages.splice(pageIndex, 1)[0];
    report.pages.splice(pageIndex + 1, 0, page);
    if (currentPageIndex.value === pageIndex) {
      currentPageIndex.value = pageIndex + 1;
    }
  }
}

function addTextBlock() {
  if (currentPage.value) {
    currentPage.value.blocks.push(
      createTextBlock("<p>Digite seu texto aqui...</p>")
    );
  }
}

function addDataBlock() {
  if (currentPage.value) {
    currentPage.value.blocks.push(
      createDataBlock("custom-data", "Dados Personalizados")
    );
  }
}

function addTableBlock() {
  if (currentPage.value) {
    currentPage.value.blocks.push(
      createTableBlock("custom-table", "Tabela Personalizada")
    );
  }
}

function addImageBlock() {
  if (currentPage.value) {
    currentPage.value.blocks.push(createImageBlock());
  }
}

function removeBlock(pageIndex: number, blockIndex: number) {
  if (report.pages[pageIndex] && report.pages[pageIndex].blocks) {
    report.pages[pageIndex].blocks.splice(blockIndex, 1);
    selectedBlock.value = null;
  }
}

function moveBlockUp(pageIndex: number, blockIndex: number) {
  if (
    blockIndex > 0 &&
    report.pages[pageIndex] &&
    report.pages[pageIndex].blocks
  ) {
    const blocks = report.pages[pageIndex].blocks;
    const block = blocks.splice(blockIndex, 1)[0];
    blocks.splice(blockIndex - 1, 0, block);
  }
}

function moveBlockDown(pageIndex: number, blockIndex: number) {
  const blocks = report.pages[pageIndex]?.blocks;
  if (blocks && blockIndex < blocks.length - 1) {
    const block = blocks.splice(blockIndex, 1)[0];
    blocks.splice(blockIndex + 1, 0, block);
  }
}

function selectBlock(block: Block) {
  selectedBlock.value = block;
}

function updateBlockData(pageIndex: number, blockIndex: number, newData: any) {
  if (report.pages[pageIndex] && report.pages[pageIndex].blocks[blockIndex]) {
    report.pages[pageIndex].blocks[blockIndex].data = {
      ...report.pages[pageIndex].blocks[blockIndex].data,
      ...newData,
    };
  }
}

function onBlockEdit(updatedBlock: Block) {
  if (selectedBlock.value) {
    Object.assign(selectedBlock.value, updatedBlock);
  }
}

function onContentEdit() {
  saveReport();
}

function onBlockReorder() {
  saveReport();
}

function updateHeader(pageIndex: number, event: Event) {
  const target = event.target as HTMLElement;
  if (report.pages[pageIndex]?.header) {
    report.pages[pageIndex].header!.title = target.innerText;
  }
}

function updateSubtitle(pageIndex: number, event: Event) {
  const target = event.target as HTMLElement;
  if (report.pages[pageIndex]?.header) {
    report.pages[pageIndex].header!.subtitle = target.innerText;
  }
}

function updateFooter(pageIndex: number, event: Event) {
  const target = event.target as HTMLElement;
  if (report.pages[pageIndex]?.footer) {
    report.pages[pageIndex].footer!.content = target.innerText;
  }
}

// Componentes dinâmicos
function getBlockComponent(type: string) {
  const components: any = {
    text: TextBlock,
    data: DataBlock,
    table: TableBlock,
    image: ImageBlock,
  };
  return components[type] || TextBlock;
}

function getEditComponent(type: string) {
  const components: any = {
    text: TextEdit,
    data: DataEdit,
    table: TableEdit,
    image: ImageEdit,
  };
  return components[type] || TextEdit;
}

// Utilitários
function generateId(): string {
  return Date.now().toString(36) + Math.random().toString(36).substr(2);
}

function saveReport() {
  try {
    localStorage.setItem("occupational-hygiene-report", JSON.stringify(report));
    console.log("Relatório salvo:", report);
  } catch (error) {
    console.error("Erro ao salvar relatório:", error);
  }
}

function exportPDF() {
  alert("Exportando para PDF...");
}

function printReport() {
  window.print();
}

// Watch para garantir que currentPageIndex seja sempre válido
watch(currentPageIndex, (newIndex) => {
  if (newIndex >= report.pages.length) {
    currentPageIndex.value = Math.max(0, report.pages.length - 1);
  }
});

watch(
  () => report.pages,
  (newPages) => {
    if (newPages.length === 0) {
      report.pages.push(createNewPage());
    }
  },
  { deep: true }
);
</script>

<template>
  <div class="report-editor">
    <!-- Menu Lateral Esquerdo -->
    <div class="left-sidebar">
      <div class="sidebar-header">
        <h3>Editor de Relatório</h3>
      </div>

      <div class="sidebar-section">
        <h4>Adicionar Blocos</h4>
        <div class="block-buttons">
          <button @click="addTextBlock" class="block-btn">
            <span class="block-icon">📝</span>
            <span class="block-label">Texto</span>
          </button>
          <button @click="addDataBlock" class="block-btn">
            <span class="block-icon">📊</span>
            <span class="block-label">Dados</span>
          </button>
          <button @click="addTableBlock" class="block-btn">
            <span class="block-icon">📋</span>
            <span class="block-label">Tabela</span>
          </button>
          <button @click="addImageBlock" class="block-btn">
            <span class="block-icon">🖼️</span>
            <span class="block-label">Imagem</span>
          </button>
        </div>
      </div>

      <div class="sidebar-section">
        <h4>Gerenciar Páginas</h4>
        <div class="page-controls">
          <button @click="addPage" class="sidebar-btn primary">
            <span>📄</span> Nova Página
          </button>
        </div>

        <div class="pages-list">
          <div
            v-for="(page, pageIndex) in report.pages"
            :key="page.id"
            class="page-item"
            :class="{ active: currentPageIndex === pageIndex }"
            @click="currentPageIndex = pageIndex"
          >
            <span class="page-number">Página {{ pageIndex + 1 }}</span>
            <div class="page-actions">
              <button
                @click.stop="movePageUp(pageIndex)"
                class="page-action-btn"
                :disabled="pageIndex === 0"
              >
                ↑
              </button>
              <button
                @click.stop="movePageDown(pageIndex)"
                class="page-action-btn"
                :disabled="pageIndex === report.pages.length - 1"
              >
                ↓
              </button>
              <button
                @click.stop="removePage(pageIndex)"
                class="page-action-btn danger"
                :disabled="report.pages.length === 1"
              >
                ×
              </button>
            </div>
          </div>
        </div>
      </div>

      <div class="sidebar-section">
        <h4>Ações</h4>
        <div class="action-buttons">
          <button @click="exportPDF" class="sidebar-btn success">
            <span>📤</span> Exportar PDF
          </button>
          <button @click="saveReport" class="sidebar-btn">
            <span>💾</span> Salvar
          </button>
          <button @click="printReport" class="sidebar-btn">
            <span>🖨️</span> Imprimir
          </button>
        </div>
      </div>
    </div>

    <!-- Área Central - Páginas do Relatório -->
    <div class="main-content">
      <div class="content-header">
        <h2>Relatório de Higiene Ocupacional</h2>
        <div class="content-actions">
          <span class="page-indicator">
            Página {{ currentPageIndex + 1 }} de {{ report.pages.length }}
          </span>
        </div>
      </div>

      <div class="pages-container">
        <div class="page-wrapper">
          <div class="a4-page" v-if="currentPage">
            <!-- Cabeçalho -->
            <div class="page-header" v-if="currentPage.header">
              <div class="header-content">
                <h1
                  contenteditable="true"
                  @blur="updateHeader(currentPageIndex, $event)"
                  @input="onContentEdit"
                >
                  {{ currentPage.header.title }}
                </h1>
                <div
                  contenteditable="true"
                  class="header-subtitle"
                  @blur="updateSubtitle(currentPageIndex, $event)"
                  @input="onContentEdit"
                >
                  {{ currentPage.header.subtitle }}
                </div>
              </div>
            </div>

            <!-- Conteúdo da Página -->
            <div class="page-content" v-if="currentPage.blocks">
              <draggable
                v-model="currentPage.blocks"
                item-key="id"
                class="blocks-container"
                @end="onBlockReorder"
                handle=".drag-handle"
              >
                <template #item="{ element: block, index: blockIndex }">
                  <div
                    :class="[
                      'block',
                      `block-${block.type}`,
                      { selected: selectedBlock?.id === block.id },
                    ]"
                    @click="selectBlock(block)"
                  >
                    <!-- Controles do Bloco -->
                    <div class="block-controls">
                      <div class="drag-handle">⠿</div>
                      <button
                        @click.stop="removeBlock(currentPageIndex, blockIndex)"
                        class="block-btn danger"
                      >
                        ×
                      </button>
                      <button
                        @click.stop="moveBlockUp(currentPageIndex, blockIndex)"
                        class="block-btn"
                        :disabled="blockIndex === 0"
                      >
                        ↑
                      </button>
                      <button
                        @click.stop="
                          moveBlockDown(currentPageIndex, blockIndex)
                        "
                        class="block-btn"
                        :disabled="blockIndex === currentPage.blocks.length - 1"
                      >
                        ↓
                      </button>
                    </div>

                    <!-- Renderização do Bloco -->
                    <component
                      :is="getBlockComponent(block.type)"
                      :block="block"
                      :pageIndex="currentPageIndex"
                      :blockIndex="blockIndex"
                      @update="
                        updateBlockData(currentPageIndex, blockIndex, $event)
                      "
                      @content-edit="onContentEdit"
                    />
                  </div>
                </template>
              </draggable>
            </div>

            <!-- Rodapé -->
            <div class="page-footer" v-if="currentPage.footer">
              <div
                contenteditable="true"
                class="footer-content"
                @blur="updateFooter(currentPageIndex, $event)"
                @input="onContentEdit"
              >
                {{ currentPage.footer.content }}
              </div>
            </div>
          </div>

          <!-- Estado de carregamento -->
          <div v-else class="loading-page">
            <p>Carregando página...</p>
          </div>
        </div>
      </div>

      <!-- Navegação entre Páginas -->
      <div class="page-navigation" v-if="report.pages.length > 1">
        <button
          @click="previousPage"
          class="nav-btn"
          :disabled="currentPageIndex === 0"
        >
          ← Página Anterior
        </button>
        <button
          @click="nextPage"
          class="nav-btn"
          :disabled="currentPageIndex === report.pages.length - 1"
        >
          Próxima Página →
        </button>
      </div>
    </div>

    <!-- Painel de Edição Direita -->
    <div v-if="selectedBlock" class="right-sidebar">
      <div class="sidebar-header">
        <h3>Editar Bloco</h3>
        <button @click="selectedBlock = null" class="close-btn">×</button>
      </div>

      <div class="edit-panel-content">
        <component
          :is="getEditComponent(selectedBlock.type)"
          :block="selectedBlock"
          @update="onBlockEdit"
        />
      </div>
    </div>

    <!-- Estado Vazio -->
    <div v-else class="right-sidebar empty">
      <div class="empty-state">
        <div class="empty-icon">📝</div>
        <h3>Selecione um Bloco</h3>
        <p>Clique em qualquer bloco para começar a editar</p>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.report-editor {
  display: grid;
  grid-template-columns: 280px 1fr 320px;
  min-height: 100vh;
  background: #f8f9fa;
}

// Sidebar Esquerda
.left-sidebar {
  background: white;
  border-right: 1px solid #e9ecef;
  overflow-y: auto;
  height: 100vh;
  position: sticky;
  top: 0;
}

.sidebar-header {
  padding: 20px;
  border-bottom: 1px solid #e9ecef;

  h3 {
    margin: 0;
    color: #333;
    font-size: 16px;
  }
}

.sidebar-section {
  padding: 20px;
  border-bottom: 1px solid #f8f9fa;

  h4 {
    margin: 0 0 15px 0;
    color: #555;
    font-size: 14px;
    font-weight: 600;
  }
}

.block-buttons {
  display: grid;
  gap: 8px;
}

.block-btn {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px;
  border: 1px solid #e9ecef;
  background: white;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s ease;
  text-align: left;

  &:hover {
    border-color: #007bff;
    background: #f8f9ff;
  }
}

.block-icon {
  font-size: 16px;
}

.block-label {
  font-size: 13px;
  font-weight: 500;
}

.sidebar-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #ddd;
  background: white;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
  margin-bottom: 8px;
  transition: all 0.3s ease;

  &:hover {
    background: #f8f9fa;
  }

  &.primary {
    background: #007bff;
    color: white;
    border-color: #007bff;

    &:hover {
      background: #0056b3;
    }
  }

  &.success {
    background: #28a745;
    color: white;
    border-color: #28a745;

    &:hover {
      background: #1e7e34;
    }
  }
}

.pages-list {
  max-height: 200px;
  overflow-y: auto;
}

.page-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 12px;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  margin-bottom: 8px;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    border-color: #007bff;
    background: #f8f9ff;
  }

  &.active {
    border-color: #007bff;
    background: #007bff;
    color: white;
  }
}

.page-number {
  font-size: 13px;
  font-weight: 500;
}

.page-actions {
  display: flex;
  gap: 2px;
}

.page-action-btn {
  padding: 2px 4px;
  border: none;
  background: transparent;
  border-radius: 3px;
  cursor: pointer;
  font-size: 10px;

  &:hover:not(:disabled) {
    background: rgba(0, 0, 0, 0.1);
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }

  &.danger:hover {
    background: #dc3545;
    color: white;
  }
}

.action-buttons {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

// Conteúdo Principal
.main-content {
  padding: 20px;
  overflow-y: auto;
  height: 100vh;
}

.content-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding: 0 20px;

  h2 {
    margin: 0;
    color: #333;
  }
}

.page-indicator {
  color: #666;
  font-size: 14px;
  font-weight: 500;
}

.pages-container {
  display: flex;
  justify-content: center;
  margin-bottom: 20px;
}

.page-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.a4-page {
  width: 794px;
  min-height: 1123px;
  background: white;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  position: relative;
}

.page-header {
  padding: 40px 40px 20px;
  border-bottom: 2px solid #e9ecef;
  text-align: center;

  h1 {
    margin: 0;
    font-size: 24px;
    color: #333;

    &:focus {
      outline: 2px dashed #007bff;
      outline-offset: 2px;
    }
  }

  .header-subtitle {
    margin-top: 10px;
    color: #666;
    font-size: 14px;

    &:focus {
      outline: 2px dashed #007bff;
      outline-offset: 2px;
    }
  }
}

.page-content {
  flex: 1;
  padding: 20px 40px;
}

.blocks-container {
  min-height: 800px;
}

.block {
  position: relative;
  margin: 15px 0;
  border: 2px solid transparent;
  border-radius: 8px;
  transition: all 0.3s ease;

  &:hover {
    border-color: #dee2e6;
  }

  &.selected {
    border-color: #007bff;
    background: #f8f9ff;
  }
}

.block-controls {
  position: absolute;
  top: -30px;
  right: 0;
  display: flex;
  gap: 2px;
  opacity: 0;
  transition: opacity 0.3s ease;

  .block:hover & {
    opacity: 1;
  }
}

.drag-handle {
  padding: 2px 6px;
  background: #6c757d;
  color: white;
  border-radius: 4px 0 0 4px;
  cursor: grab;
  font-size: 12px;
}

.block-btn {
  padding: 2px 6px;
  background: #6c757d;
  color: white;
  border: none;
  font-size: 12px;
  cursor: pointer;

  &:hover {
    background: #545b62;
  }

  &.danger:hover {
    background: #dc3545;
  }
}

.page-footer {
  padding: 20px 40px;
  border-top: 1px solid #e9ecef;
  text-align: center;
  color: #666;
  font-size: 12px;

  .footer-content:focus {
    outline: 2px dashed #007bff;
    outline-offset: 2px;
  }
}

.page-navigation {
  display: flex;
  justify-content: space-between;
  padding: 0 20px;
}

.nav-btn {
  padding: 10px 20px;
  border: 1px solid #ddd;
  background: white;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover:not(:disabled) {
    border-color: #007bff;
    background: #f8f9ff;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

// Sidebar Direita
.right-sidebar {
  background: white;
  border-left: 1px solid #e9ecef;
  overflow-y: auto;
  height: 100vh;
  position: sticky;
  top: 0;

  &.empty {
    display: flex;
    align-items: center;
    justify-content: center;
  }
}

.empty-state {
  text-align: center;
  color: #666;

  .empty-icon {
    font-size: 48px;
    margin-bottom: 16px;
  }

  h3 {
    margin: 0 0 8px 0;
    color: #333;
  }

  p {
    margin: 0;
    font-size: 14px;
  }
}

.edit-panel-content {
  padding: 20px;
}

// Estado de carregamento
.loading-page {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  color: #666;
  font-size: 16px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

// Responsividade
@media (max-width: 1200px) {
  .report-editor {
    grid-template-columns: 250px 1fr 300px;
  }

  .a4-page {
    width: 100%;
    min-height: auto;
  }
}

@media print {
  .left-sidebar,
  .right-sidebar,
  .content-header,
  .page-navigation,
  .block-controls {
    display: none !important;
  }

  .report-editor {
    grid-template-columns: 1fr;
  }

  .main-content {
    padding: 0;
  }

  .a4-page {
    box-shadow: none;
    margin: 0;
    page-break-after: always;
  }

  .block {
    border: none !important;
  }
}
</style>
